import React, { useState, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { Form, Button, Card, Space, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { CustomForm } from "ui/Form"
import { useWatch } from "antd/es/form/Form"
import ClientDetail from "./ClientDetail"
import ExperienceDetail from "./ExperienceDetail"
import PaymentMethod from "./PaymentMethod"
import PriceBreakdown from "./PriceBreakdown"
import styles from "./BookingForm.module.css"
import { useSelector, useDispatch } from "react-redux"
import { calculateBookingTotal } from "../helpers/calculateBookingTotal"
import { updateBookingSelections } from "store/experiences"

const { Title } = Typography

const BookingForm = ({ form, bookingUuid, bookAgain, booking, onSubmit, onCancelModal }) => {
  const { t } = useTranslation()
  const [existingClientId, setExistingClientId] = useState(null)

  const {
    experienceById: { experienceDetail },
    experienceBookingData: { bookingSelections }
  } = useSelector((state) => state.experiences)
  const dispatch = useDispatch()
  const mode = "guideBooking"
  const clientName = useWatch("existing_client", form)
  const clientFirstName = useWatch("firstName", form)
  const clientLastName = useWatch("lastName", form)
  const clientEmail = useWatch("email", form)
  const clientPhone = useWatch("phone_number", form)
  const [alertMessage, setAlertMessage] = useState("")
  const isClientDetailsFilled = !!(((clientFirstName && clientLastName) || clientName) && clientEmail && clientPhone) && !alertMessage

  // Calculate booking total when selections change
  const { total, breakdown } = useMemo(() => {
    const tripPricing = bookingSelections?.tripLength?.price
    const counts = bookingSelections?.counts

    if (!tripPricing || !counts) {
      return { total: 0, breakdown: [] }
    }

    return calculateBookingTotal(
      bookingSelections,
      counts,
      experienceDetail?.guide_booking_fee_percentage || booking?.booking_fee_percentage
    )
  }, [bookingSelections, experienceDetail?.guide_booking_fee_percentage, booking?.booking_fee_percentage])

  // Update Redux only when the total changes
  useEffect(() => {
    if (total !== bookingSelections?.amount) {
      dispatch(updateBookingSelections({ amount: total, breakdown }))
    }
  }, [total, breakdown, bookingSelections?.amount, dispatch])

  return (
    <CustomForm layout="vertical" form={form} onFinish={onSubmit}>
      <div className={styles.scrollContainer}>
        {/* Client Details */}
        <Card className={styles.cardContainer}>
          <Title className={styles.cardTitle} level={5}>
            {t("booking.client_detail")}
          </Title>
          <ClientDetail
            setExistingClientId={setExistingClientId}
            form={form}
            bookingId={bookingUuid}
            alertMessage={alertMessage}
            setAlertMessage={setAlertMessage}
            booking={booking}
          />
        </Card>

        {/* Experience Details */}
        <Card className={styles.cardContainer}>
          <Title className={styles.cardTitle} level={5}>
            {t("booking.experience_details")}
          </Title>
          <ExperienceDetail form={form} bookingId={bookingUuid} booking={booking} bookAgain={bookAgain} mode={mode} />
        </Card>

        {/* Payment Method */}
        {isClientDetailsFilled && (
          <Card className={styles.cardContainer}>
            <Title className={styles.cardTitle} level={5}>
              {t("booking.payment_method")}
            </Title>
            <PaymentMethod
              existingClientId={existingClientId}
              setExistingClientId={setExistingClientId}
              form={form}
              bookingId={bookingUuid}
              booking={booking}
              bookAgain={bookAgain}
            />
          </Card>
        )}

        {/* Price Breakdown */}
        <Card className={styles.cardContainer}>
          <Title className={styles.cardTitle} level={5}>
            {t("booking.price_breakdown")}
          </Title>
          <PriceBreakdown form={form} booking={booking} />
        </Card>
      </div>
      <Form.Item className={styles.formActions}>
        <Space>
          <Button variant="outlined" color="primary" onClick={() => onCancelModal()}>
            {t("global.cancel")}
          </Button>
          <Button type="primary" htmlType="submit">
            {bookingUuid && !bookAgain ? t("global.update") : t("global.submit")}
          </Button>
        </Space>
      </Form.Item>
    </CustomForm>
  )
}

BookingForm.propTypes = {
  form: PropTypes.object.isRequired,
  bookingUuid: PropTypes.string.isRequired,
  bookAgain: PropTypes.object.isRequired,
  booking: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancelModal: PropTypes.func.isRequired
}

export default BookingForm
